import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Award, MapPin, ShieldCheck, Gem, LayoutGrid, Layers, Sofa, Trees, Square, Flame, Columns, Mountain, Snowflake } from "lucide-react";
import api from "../lib/api";
import { FadeUp, Counter } from "../components/Motion";
import BackyardExplorer from "../components/BackyardExplorer";
import LeadForm from "../components/LeadForm";
import TrustStrip from "../components/TrustStrip";
import GoogleReviews from "../components/GoogleReviews";
import { LocalBusinessSchema } from "../components/SeoSchema";
import PageMeta from "../components/PageMeta";

const SERVICE_ICONS = {
  LayoutGrid, Layers, Sofa, Trees, Square, Flame, Columns, Mountain,
};

const HERO_VIDEO = "/hero.mp4";

// Visible September–November: book snow removal before the first snowfall
const isSnowBookingSeason = () => {
  const m = new Date().getMonth();
  return m >= 8 && m <= 10;
};

const SnowSeasonBanner = () => {
  if (!isSnowBookingSeason()) return null;
  return (
    <section data-testid="snow-season-banner" className="bg-[#8C1D30] border-y border-[#151515]/20">
      <div className="px-6 md:px-12 lg:px-16 py-8 md:py-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
        <div className="flex items-start gap-4">
          <Snowflake className="w-8 h-8 text-[#F5F3EF] shrink-0 mt-1" />
          <div>
            <div className="font-serif-display text-2xl md:text-3xl leading-tight text-[#F5F3EF]">
              Snow season is coming.
            </div>
            <p className="text-sm md:text-base text-[#F5F3EF]/80 mt-1">
              Book your commercial snow removal before the first snowfall — seasonal contracts are filling fast.
            </p>
          </div>
        </div>
        <Link
          to="/commercial/snow-removal"
          data-testid="snow-season-banner-cta"
          className="shrink-0 inline-flex items-center gap-2 px-8 py-4 bg-[#F5F3EF] text-[#151515] hover:bg-[#0B2B1F] hover:text-[#F5F3EF] text-xs uppercase tracking-widest transition-colors"
        >
          Reserve Your Spot <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );
};

export default function Home() {
  const [projects, setProjects] = useState([]);
  const [services, setServices] = useState([]);

  useEffect(() => {
    api.get("/projects?featured=true").then((r) => setProjects(r.data));
    api.get("/services?category=residential").then((r) => setServices(r.data));
  }, []);

  return (
    <div data-testid="home-page">
      <PageMeta
        title="NorthCrest Landscaping — Premium Outdoor Living in Edmonton, Alberta"
        description="Award-winning landscape design and construction in Edmonton — paver patios, fire features, outdoor kitchens, retaining walls, fences, and lighting. Free estimate."
        path="/"
        image="https://images.unsplash.com/photo-1775733923991-e7223f9f44bc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
      />
      <LocalBusinessSchema />
      {/* HERO */}
      <section className="relative h-screen w-full overflow-hidden">
        <video
          className="absolute inset-0 w-full h-full object-cover"
          src={HERO_VIDEO}
          autoPlay muted loop playsInline
          poster="https://images.unsplash.com/photo-1775733923991-e7223f9f44bc?crop=entropy&cs=srgb&fm=jpg&q=85"
        />
        <div className="absolute inset-0 hero-overlay" />

        <div className="relative h-full flex flex-col justify-end pb-24 px-6 md:px-12 lg:px-16">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.2, ease: [0.22,1,0.36,1] }} className="max-w-5xl">
            <div className="overline text-[#D8D6D0]/80 mb-6">Edmonton · Alberta · Est. 2020</div>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-tight">
              Professional Landscaping<br/>
              <span className="italic text-[#D8D6D0]">& Outdoor Living</span><br/>
              in Alberta.
            </h1>
            <p className="mt-8 text-lg md:text-xl text-[#D8D6D0]/80 font-light tracking-wide max-w-xl">
              Design. Build. Transform.
            </p>
            <div className="mt-12 flex flex-col sm:flex-row gap-4">
              <Link to="/estimate" data-testid="hero-cta-estimate" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#8C1D30] hover:bg-[#7A1829] text-[#F5F3EF] text-sm tracking-wider uppercase transition-colors duration-300">
                Get Free Estimate <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link to="/yard-designer" data-testid="hero-cta-designer" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#2E7D4F] hover:bg-[#256B42] text-[#F5F3EF] text-sm tracking-wider uppercase transition-colors duration-300">
                Design My Yard
              </Link>
            </div>
          </motion.div>
        </div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[#D8D6D0]/50 text-xs tracking-widest uppercase animate-float">Scroll</div>
      </section>

      {/* TRUST STRIP — live social proof under hero */}
      <TrustStrip />

      {/* SEASONAL SNOW BANNER (Sep–Nov) */}
      <SnowSeasonBanner />

      {/* TRUST */}
      <section className="px-6 md:px-12 lg:px-16 py-24 border-y border-[#D8D6D0]/10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-12">
          {[
            { icon: Award, n: 200, suffix: "+", label: "Projects Completed" },
            { icon: MapPin, n: 12, suffix: "+", label: "Service Areas" },
            { icon: ShieldCheck, n: 100, suffix: "%", label: "Licensed & Insured" },
            { icon: Gem, n: 6, suffix: "+ yrs", label: "Craftsmanship" },
          ].map((s, i) => (
            <FadeUp key={s.label} delay={i * 0.1}>
              <div>
                <s.icon className="w-5 h-5 text-[#8C1D30] mb-4" />
                <div className="font-serif-display text-5xl md:text-6xl">
                  <Counter value={s.n} suffix={s.suffix} />
                </div>
                <div className="overline text-[#D8D6D0]/50 mt-2">{s.label}</div>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="px-6 md:px-12 lg:px-16 py-32">
        <FadeUp>
          <div className="max-w-3xl mb-20">
            <div className="overline text-[#8C1D30] mb-4">Services</div>
            <h2 className="font-serif-display text-4xl md:text-6xl leading-none">
              Architectural outdoor living, <span className="italic text-[#D8D6D0]">composed with intention.</span>
            </h2>
          </div>
        </FadeUp>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#D8D6D0]/10">
          {services.slice(0, 8).map((s, i) => {
            const Icon = SERVICE_ICONS[s.icon] || LayoutGrid;
            return (
              <FadeUp key={s.id} delay={i * 0.05}>
                <Link to={`/services/detail/${s.slug}`} data-testid={`service-card-${s.slug}`} className="group block bg-[#151515] p-8 lg:p-10 h-full hover:bg-[#0B2B1F] transition-colors duration-500">
                  <Icon
                    className="w-8 h-8 md:w-10 md:h-10 text-[#D8D6D0]/70 mb-8 group-hover:text-[#8C1D30] group-hover:scale-110 transition-all duration-500"
                    strokeWidth={1.25}
                  />
                  <div className="font-serif-display text-2xl md:text-3xl mb-4">{s.name}</div>
                  <p className="text-sm text-[#D8D6D0]/60 leading-relaxed">{s.short_description}</p>
                  <div className="mt-8 flex items-center gap-2 text-xs uppercase tracking-widest text-[#D8D6D0]/60 group-hover:text-[#F5F3EF] transition-colors">
                    Explore <ArrowUpRight className="w-3 h-3" />
                  </div>
                </Link>
              </FadeUp>
            );
          })}
        </div>
      </section>

      {/* FEATURED PROJECTS */}
      <section className="px-6 md:px-12 lg:px-16 py-32 bg-[#0d0d0d]">
        <FadeUp>
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
            <div>
              <div className="overline text-[#8C1D30] mb-4">Selected Work</div>
              <h2 className="font-serif-display text-4xl md:text-6xl">Recent transformations.</h2>
            </div>
            <Link to="/projects" data-testid="all-projects-link" className="link-underline text-sm uppercase tracking-widest">View All Projects</Link>
          </div>
        </FadeUp>

        <div className="grid grid-cols-12 gap-6">
          {projects.slice(0, 4).map((p, i) => (
            <FadeUp key={p.id} delay={i * 0.08} className={
              i === 0 ? "col-span-12 md:col-span-8" :
              i === 1 ? "col-span-12 md:col-span-4" :
              i === 2 ? "col-span-12 md:col-span-5" : "col-span-12 md:col-span-7"
            }>
              <Link to="/projects" data-testid={`featured-project-${i}`} className="group block relative overflow-hidden">
                <div className="aspect-[4/3] overflow-hidden bg-[#0B2B1F]">
                  <img src={p.image_url} alt={p.title} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-[#151515]/90 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <div className="overline text-[#D8D6D0]/70 mb-2">{p.location}</div>
                  <div className="font-serif-display text-2xl md:text-3xl">{p.title}</div>
                </div>
              </Link>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* 3D EXPLORER */}
      <section className="px-6 md:px-12 lg:px-16 py-32">
        <FadeUp>
          <div className="max-w-3xl mb-12">
            <div className="overline text-[#8C1D30] mb-4">Interactive</div>
            <h2 className="font-serif-display text-4xl md:text-6xl leading-none">
              Explore your <span className="italic text-[#D8D6D0]">future backyard</span> in 3D.
            </h2>
            <p className="text-[#D8D6D0]/60 mt-6 max-w-xl">
              Rotate, zoom, and toggle features to visualize the elements that will shape your outdoor space.
            </p>
          </div>
        </FadeUp>
        <FadeUp delay={0.2}>
          <BackyardExplorer />
        </FadeUp>
      </section>

      {/* COMMERCIAL */}
      <section className="relative px-6 md:px-12 lg:px-16 py-32 bg-[#0B2B1F]">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <FadeUp>
            <div className="overline text-[#D8D6D0]/70 mb-6">Commercial Division</div>
            <h2 className="font-serif-display text-4xl md:text-6xl leading-none">
              Landscapes that elevate <span className="italic">commercial assets.</span>
            </h2>
            <p className="text-[#D8D6D0]/80 mt-8 leading-relaxed max-w-lg">
              Developers, retail, office, industrial, condo boards, and property managers across Alberta partner with NorthCrest for landscape construction and full-service maintenance.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              {["Developers", "Retail", "Office", "Industrial", "Condo Boards", "Property Managers"].map((t) => (
                <span key={t} className="px-4 py-2 border border-[#F5F3EF]/30 text-xs uppercase tracking-widest text-[#F5F3EF]">{t}</span>
              ))}
            </div>
            <Link to="/contact?type=commercial" data-testid="commercial-cta" className="inline-flex items-center gap-2 mt-10 px-8 py-4 bg-[#8C1D30] hover:bg-[#7A1829] text-[#F5F3EF] text-sm tracking-wider uppercase">
              Request Commercial Proposal <ArrowUpRight className="w-4 h-4" />
            </Link>
          </FadeUp>
          <FadeUp delay={0.2}>
            <div className="aspect-[4/5] overflow-hidden">
              <img src="https://images.unsplash.com/photo-1773193120818-db5e2d137acc?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Commercial" className="w-full h-full object-cover" />
            </div>
          </FadeUp>
        </div>
      </section>

      {/* GOOGLE REVIEWS */}
      <GoogleReviews compact />

      {/* FINAL CTA */}
      <section className="px-6 md:px-12 lg:px-16 py-32 border-t border-[#D8D6D0]/10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <FadeUp>
            <div className="overline text-[#8C1D30] mb-4">Begin</div>
            <h2 className="font-serif-display text-4xl md:text-6xl leading-none">
              Tell us about your <span className="italic text-[#D8D6D0]">project.</span>
            </h2>
            <p className="text-[#D8D6D0]/60 mt-6 max-w-md">
              A senior designer will respond within one business day with next steps and a complimentary consultation.
            </p>
          </FadeUp>
          <FadeUp delay={0.15}>
            <LeadForm leadType="residential" source="home" compact />
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
